/**
 * Контракты публичного предварительного расчёта (E07.6, ФТ-1.6).
 * Выражения формул клиенту не отдаются (И-1).
 */

import { z } from "zod";
import { apiSuccessSchema } from "./envelope";
import { resultTierSchema } from "./attributes";

const nameSchema = z.string().trim().min(1).max(255);

/** Тело `POST /api/v1/calc/preview`. */
export const calcPreviewRequestSchema = z.object({
  /** Гостевой проект wizard; нужен для лимита проходов гостя в режиме "за модель". */
  projectId: z.string().uuid().optional(),
  modelId: z.string().uuid(),
  /** Предпочтительный типоразмер с шага 2; константы unit подмешиваются во входы. */
  unitId: z.string().uuid().nullable().optional(),
  /** Значения атрибутов формы (ключ — attributeId). */
  values: z.record(z.string().uuid(), z.string().max(512)),
});

export type CalcPreviewRequest = z.infer<typeof calcPreviewRequestSchema>;

/** Величина промежуточного результата с `show_in_result`. */
export const calcPreviewOutputSchema = z.object({
  attributeId: z.string().uuid(),
  name: nameSchema,
  formulaName: z.string().min(1).max(128).nullable(),
  unitName: nameSchema.nullable(),
  value: z.string().max(512),
  resultTier: resultTierSchema.nullable(),
});

export type CalcPreviewOutput = z.infer<typeof calcPreviewOutputSchema>;

/**
 * Параметр с `resultTier=full`, скрытый из‑за отсутствия допуска.
 * Значение не передаётся (И-12): клиент показывает «доступ по запросу».
 */
export const calcPreviewRestrictedOutputSchema = z.object({
  attributeId: z.string().uuid(),
  name: nameSchema,
  formulaName: z.string().min(1).max(128).nullable(),
  unitName: nameSchema.nullable(),
  resultTier: z.literal("full"),
  accessHint: z.literal("request_required"),
});

export type CalcPreviewRestrictedOutput = z.infer<typeof calcPreviewRestrictedOutputSchema>;

/** Ответ предварительного расчёта (ФТ-1.6 / ФТ-4.3). */
export const calcPreviewResultSchema = z.object({
  modelId: z.string().uuid(),
  modelName: nameSchema,
  unitId: z.string().uuid().nullable(),
  unitCode: z.string().min(1).max(128).nullable(),
  outputs: z.array(calcPreviewOutputSchema),
  /** Full-tier без значений — только метаданные для UI «доступ по запросу». */
  restrictedOutputs: z.array(calcPreviewRestrictedOutputSchema).default([]),
});

export type CalcPreviewResult = z.infer<typeof calcPreviewResultSchema>;

export const calcPreviewResultResponseSchema = apiSuccessSchema(calcPreviewResultSchema);

export const CALC_ERROR_CODES = {
  NOT_FOUND: "CALC_NOT_FOUND",
  VALIDATION: "CALC_VALIDATION",
  FAILED: "CALC_FAILED",
} as const;

export type CalcApiErrorCode = (typeof CALC_ERROR_CODES)[keyof typeof CALC_ERROR_CODES];
